import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import { getCurrentCandidate } from '@/CandidateExperience/selectors/candidate'

import { fetchRecommendationFeedback } from './api'

/**
 * Custom hook for determining the visibility of the recommendation feedback banner.
 *
 * Visibility is determined using the presence of the 'show_feedback_form' query parameter in the URL
 * and whether the current candidate has submitted feedback for the position being viewed.
 *
 * @returns {Object} The recommendation feedback banner visibility state.
 * @property {boolean} isVisible - Indicates whether the feedback form should be visible or not.
 *
 * @example
 *  const { isVisible } = useRecommendationFeedbackBannerVisibility()
 */
const useRecommendationFeedbackBannerVisibility = () => {
  const { jobId: positionId } = useParams()
  const { id: candidateProfileId } = useSelector(getCurrentCandidate)
  const { search } = useLocation()

  let isVisible = search.includes('show_feedback_form')

  const { data, isLoading, isFetchedAfterMount } = useQuery(
    ['recommendationFeedback', positionId, candidateProfileId],
    () => fetchRecommendationFeedback({ candidateProfileId, positionId }),
    {
      enabled: isVisible,
      retry: false,
    },
  )

  // Ensure that isVisible is always false until fresh data is fetched.
  // Without this, the candidate will see the banner even if they have
  // already submitted feedback - unless they refresh the page - while
  // the data is being fetched.
  const isDataStale = !isFetchedAfterMount

  if (isLoading || isDataStale || data?.position?.feedback) {
    isVisible = false
  }

  return {
    isVisible,
  }
}

export default useRecommendationFeedbackBannerVisibility
